import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import QRCode from "qrcode";
import { HashLoader } from "react-spinners";
import { generateQRData } from "../Helper/utils";
import { useQRModal } from "../../../Context/QrContext";
import "./qrgen.css";

const QRCodeGenerator = ({ openQRModal, handleCloseQRModal }) => {
  const [printerCharacteristic, setPrinterCharacteristic] = useState(null);
  const [device, setDevice] = useState(null);
  const printerRef = useRef({ device: null, characteristic: null });
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [bitmapData, setBitmapData] = useState(null);
  const previewCanvasRef = useRef(null);
  const [copyCount, setCopyCount] = useState(1);
  const {
    QRmodalOpen,
    closeQRModal,
    QRdata,
    setPrintedRows,
    printedRows,
    rowIndex,
    submitHandler,
    page,
    setPrintStatus,
    printStatus,
  } = useQRModal();
  console.log("7547585", page);

  // const QRdata = generateQRData(QRdatas);
  const dpi = 203;
  const labelWidthMM = 55;
  const labelHeightMM = 40;

  const labelConfig = {
    width: Math.round((72.6 / 25.4) * dpi),
    height: Math.round((40 / 25.4) * dpi),
    gapHeight: 10,
    offsetX: 10,
    offsetY: 11,
    font: 0.06,
  };

  const bluetoothServiceUUID = "e7810a71-73ae-499d-8c15-faa9aef0c3f2";
  const bluetoothCharacteristicUUID = "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f";

  const isBluetoothSupported = () => !!navigator.bluetooth;

  const scanForBluetoothPrinter = async () => {
    if (!isBluetoothSupported()) {
      alert("Web Bluetooth API is not supported.");
      return;
    }
    try {
      setIsConnecting(true);
      const foundDevice = await navigator.bluetooth.requestDevice({
        filters: [{ services: [bluetoothServiceUUID] }],
        optionalServices: [bluetoothServiceUUID],
      });
      setDevice(foundDevice);
      await connectToPrinter(foundDevice);
    } catch (error) {
      alert(`Bluetooth scan failed: ${error.message}`);
    } finally {
      setIsConnecting(false);
    }
  };

  const connectToPrinter = async (connectedDevice) => {
    try {
      const server = await connectedDevice.gatt.connect();
      const service = await server.getPrimaryService(bluetoothServiceUUID);
      const characteristic = await service.getCharacteristic(
        bluetoothCharacteristicUUID
      );
      printerRef.current = {
        device: connectedDevice,
        characteristic,
      };
      setPrinterCharacteristic(characteristic);
      setDevice(connectedDevice);
      localStorage.setItem("printerId", connectedDevice.id);
    } catch (error) {
      alert(`Connection failed: ${error.message}`);
      printerRef.current = { device: null, characteristic: null };
      setPrinterCharacteristic(null);
      setDevice(null);
    }
  };

  const drawQRAndTextToCanvas = async (qrData) => {
    const canvas = document.createElement("canvas");
    const { width, height, offsetX, offsetY, font } = labelConfig;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.translate(offsetX, offsetY);

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const topSectionHeight = height * 0.9; // 90% for QR and text
    const bottomSectionHeight = height * 0.01;

    // const topSectionHeight = height * 0.8;
    const qrAreaWidth = width * 0.5;
    const textAreaWidth = width * 0.5;
    const textX = qrAreaWidth - 50;
    const textMaxWidth = textAreaWidth - 20;
    const qrSize = qrAreaWidth - 40;
    const qrX = 0;
    const qrY = 0;

    const qrCanvas = document.createElement("canvas");
    qrCanvas.width = qrSize;
    qrCanvas.height = qrSize;

    await QRCode.toCanvas(qrCanvas, JSON.stringify(qrData), { width: qrSize });
    ctx.drawImage(qrCanvas, qrX, qrY);

    const fontSize = Math.floor(topSectionHeight * font);
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textBaseline = "top";
    ctx.fillStyle = "#000";
    const lineHeight = fontSize * 1.7;

    const textTopPadding = 10;
    let currentY = textTopPadding + fontSize;

    // Text wrapping helper
    const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
      text = String(text);
      const words = text.split(" ");
      let line = "";
      let currentY = y;

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, x, currentY);
          line = words[n] + " ";
          currentY += lineHeight;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, x, currentY);
      return currentY + lineHeight;
    };

    const lines = [
      qrData.PONumber || "",
      qrData.Itemcode || "",
      qrData.ItemName || "",
      qrData.CreatedDate || "",
      qrData.ActualWt || "",
      qrData.QtyNos || "",
    ];

    lines.forEach((line) => {
      currentY = wrapText(ctx, line, textX, currentY, textMaxWidth, lineHeight);
    });

    if (qrData.BatchNumber) {
      ctx.font = `bold ${fontSize}px Arial`;
      // const batchY = topSectionHeight + (bottomSectionHeight) + (fontSize);
      const batchY = topSectionHeight - 40;
      ctx.fillText(qrData.BatchNumber, 15, batchY);
    }

    // if (qrData.BatchNumber) {
    //   const batchY = qrY + qrSize + 5;
    //   ctx.fillText(qrData.BatchNumber, qrX, batchY);
    // }

    if (previewCanvasRef.current) {
      previewCanvasRef.current.width = canvas.width;
      previewCanvasRef.current.height = canvas.height;
      const previewCtx = previewCanvasRef.current.getContext("2d");
      previewCtx.drawImage(canvas, 0, 0);
    }

    return canvas;
  };

  const convertCanvasToTSPLBitmap = (canvas) => {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const bytesPerLine = Math.ceil(canvas.width / 8);
    const data = new Uint8Array(bytesPerLine * canvas.height);

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const i = (y * canvas.width + x) * 4;
        const brightness = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
        const bit = brightness < 128 ? 1 : 0;
        const byteIndex = y * bytesPerLine + (x >> 3);
        data[byteIndex] |= bit << (7 - (x % 8));
      }
    }

    for (let i = 0; i < data.length; i++) {
      data[i] = ~data[i] & 0xff;
    }

    return { data, width: canvas.width, height: canvas.height, bytesPerLine };
  };

  const sendTSPLBitmapToPrinter = async (
    characteristic,
    bitmapData,
    copies = 1
  ) => {
    const { data, height, bytesPerLine } = bitmapData;
    const encoder = new TextEncoder();

    const tsplSetup = [
      `SIZE ${labelWidthMM} mm,${labelHeightMM} mm`,
      `GAP ${labelConfig.gapHeight} mm,0 mm`,
      `DENSITY 13`,
      `SPEED 4`,
      `DIRECTION 1`,
      `REFERENCE 0,0`,
      `OFFSET 0 mm`,
      `CLS`,
      `BITMAP 0,0,${bytesPerLine},${height},0,`,
    ].join("\r\n");

    const footer = `\r\nPRINT ${copies}\r\n`;
    const headerBytes = encoder.encode(tsplSetup);
    const footerBytes = encoder.encode(footer);

    const fullData = new Uint8Array(
      headerBytes.length + data.length + footerBytes.length
    );
    fullData.set(headerBytes, 0);
    fullData.set(data, headerBytes.length);
    fullData.set(footerBytes, headerBytes.length + data.length);

    await sendChunksToPrinter(characteristic, fullData);
  };

  const sendChunksToPrinter = async (characteristic, data, chunkSize = 20) => {
    for (let offset = 0; offset < data.length; offset += chunkSize) {
      const chunk = data.slice(offset, offset + chunkSize);
      if (characteristic.properties.writeWithoutResponse) {
        await characteristic.writeValueWithoutResponse(chunk);
      } else {
        await characteristic.writeValue(chunk);
      }
    }
  };
  const getFormattedQRString = (qrData) => {
    return [
      `PONumber: ${qrData.PONumber}`,
      `Itemcode: ${qrData.Itemcode}`,
      // `ItemName: ${qrData.ItemName}`,
      `BatchNumber: ${qrData.BatchNumber}`,
      // `ActualWt: ${qrData.ActualWt}`,
      // `CreatedDate: ${qrData.CreatedDate}`,
      `CreatedDate: ${
        qrData?.CreatedDate
          ? new Date(qrData?.CreatedDate).toLocaleDateString("en-GB")
          : ""
      }`,
    ].join("\n"); // newline-separated
  };

  const buildTSPLQRCodeCommand = (qrData, copies = 1) => {
    const encoder = new TextEncoder();
    const qrContent = getFormattedQRString(qrData);
    const qrJson = JSON.stringify({
      PONumber: qrData.PONumber,
      Itemcode: qrData.Itemcode,
      ItemName: qrData.ItemName,
      BatchNumber: qrData.BatchNumber,
      ActualWt: qrData.ActualWt,
      CreatedDate: qrData.CreatedDate,
      QtyNos: qrData.QtyNos || ""
    });
    const escapeText = (text) =>
      (text || "")
        .replace(/\\/g, "\\\\") // escape backslashes
        .replace(/"/g, "'");
    // const qrContent = qrJs
    // const qrContent = qrJson.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

    const tspl =
      [
        `SIZE ${labelWidthMM} mm,${labelHeightMM} mm`,
        `GAP ${labelConfig.gapHeight} mm,0 mm`,
        `DENSITY 13`,
        `SPEED 3`,
        `DIRECTION 1`,
        `REFERENCE 0,0`,
        `CLS`,
        // Add QR code
        // `QRCODE 50,50,L,5,A,0,"${JSON.stringify(qrData)}"`,
        `QRCODE 40,40,L,5,A,0,"${qrContent}"`,
        // Add text (adjust X, Y)
        `TEXT 255,50,"2",0,1,1,"${qrData.PONumber || ""}"`,
        `TEXT 255,80,"2",0,1,1,"${qrData.Itemcode || ""}"`,
        `TEXT 255,110,"1",0,1,1,"${escapeText(qrData.ItemName) || ""}"`,
        `TEXT 255,140,"2",0,1,1,"${qrData.CreatedDate || ""}"`,
        `TEXT 255,170,"2",0,1,1,"${qrData.ActualWt || ""}"`,
        `TEXT 50,280,"2",0,1,1,"${qrData.BatchNumber || ""}"`,
         `TEXT 255,200,"2",0,1,1,"Qty: ${qrData.QtyNos || ""}"`,
        `PRINT ${copies}`,
      ].join("\r\n") + "\r\n";

    return encoder.encode(tspl);
  };
  const sendTSPLCommandToPrinter = async (characteristic, commandBytes) => {
    await sendChunksToPrinter(characteristic, commandBytes);
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    if (page === "invPosting") {
      setPrintStatus(true); // ✅ Show submit button
    } else {
      setPrintedRows((prev = []) => {
        const updated = [...prev];

        while (updated.length <= indexToPrint) {
          updated.push(false);
        }

        updated[indexToPrint] = true;

        return updated;
      });

      if (submitHandler && typeof submitHandler === "function") {
        submitHandler();
      }
    }
    const indexToPrint = rowIndex;
    setIsConnecting(true);
    const canvas = document.querySelector("canvas");
    const dataUrl = canvas.toDataURL("image/png");
    try {
      if (
        !printerRef.current.characteristic ||
        !printerRef.current.device?.gatt?.connected
      ) {
        await scanForBluetoothPrinter();
        // if (!printerCharacteristic && !device?.gatt?.connected) {
        //     alert("Printer not connected.");
        //     return;
        // }
      }

      if (!bitmapData) {
        alert("Label not ready. Please try again.");
        return;
      }
      const tsplBytes = buildTSPLQRCodeCommand(QRdata, copyCount);

      await sendTSPLCommandToPrinter(
        printerRef.current.characteristic,
        tsplBytes
      );
      // await sendTSPLBitmapToPrinter(
      //   printerRef.current.characteristic,
      //   bitmapData,
      //   copyCount
      // );
      const estimatedTime = copyCount * 1800;
      await new Promise((res) => setTimeout(res, estimatedTime));
      //   await sendTSPLBitmapToPrinter(printerCharacteristic, bitmapData);
      if (page === "invPosting") {
        setPrintStatus(true); // ✅ Show submit button
      } else {
        setPrintedRows((prev = []) => {
          const updated = [...prev];

          while (updated.length <= indexToPrint) {
            updated.push(false);
          }

          updated[indexToPrint] = true;

          return updated;
        });

        if (submitHandler && typeof submitHandler === "function") {
          submitHandler();
        }
      }
    } catch (error) {
      alert(`Printing failed: ${error.message}`);
    } finally {
      setIsPrinting(false);
      setIsConnecting(false);
    }
  };

  const handleClose = () => {
    closeQRModal();
  };

  const lastQRRef = useRef(null);

  useEffect(() => {
    if (!QRmodalOpen) {
      lastQRRef.current = null;

      return;
    }

    const prepare = async () => {
      if (!QRmodalOpen || !QRdata) return;

      const qrString = JSON.stringify(QRdata);
      if (lastQRRef.current === qrString) return; // Skip if same data
      lastQRRef.current = qrString;

      const canvas = await drawQRAndTextToCanvas(QRdata);
      if (canvas) {
        const bitmap = convertCanvasToTSPLBitmap(canvas);
        setBitmapData(bitmap);
      }
    };
    prepare();
  }, [QRmodalOpen, QRdata]);

  return (
    <Dialog
      open={QRmodalOpen}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return; // Prevent closing
        }
        handleClose(); // Only call close when needed (e.g., from a button)
      }}
      className="printermodal"
    >
      <DialogTitle className="Modal-heading">QR Code</DialogTitle>
      <DialogContent className="custom-dialog-content">
        <div className="canvas-preview-wrapper">
          <canvas
            ref={previewCanvasRef}
            className="canvas-preview"
            // style={{ border: "1px solid #ccc", marginTop: "20px" }}
          />
        </div>
      </DialogContent>
      <DialogActions className="custom-dialog-actions">
        <Button onClick={handleClose} className="Modal-cancel">
          Close
        </Button>
        <Button
          onClick={handlePrint}
          disabled={isPrinting}
          className="Modal-submit"
        >
          {isPrinting ? "Printing..." : "Print"}
        </Button>
        {page === "invPosting" && printStatus && (
          <Button onClick={() => submitHandler?.()} className="Modal-submit">
            Submit
          </Button>
        )}
      </DialogActions>
      {isConnecting && (
        <div className="loader">
          <HashLoader color="#ed1c24" />
        </div>
      )}
    </Dialog>
  );
};

export default QRCodeGenerator;
